import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  LinearProgress,
  Paper,
  styled
} from "@material-ui/core";

export const StyledAccordion = styled(Accordion)(({ theme }) => ({
  borderRadius: "0.5rem",
  marginBottom: "1rem",
  boxShadow: theme.shadows[3],
  "&:before": {
    display: "none"
  },
  "&.Mui-expanded": {
    margin: "0 0 1rem 0"
  },
  "&:last-child": {
    marginBottom: 0
  }
}));

export const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  backgroundColor: "#FFFFFF",
  borderRadius: "0.5rem",
  minHeight: "56px",
  padding: "0",
  overflow: "hidden",
  transition: "background-color 0.3s ease",
  "&:hover": {
    backgroundColor: "#F3F8E6"
  },
  "&.Mui-expanded": {
    minHeight: "56px",
    borderRadius: "0.5rem 0.5rem 0 0",
    backgroundColor: "#F3F8E6"
  },
  "& .MuiAccordionSummary-content": {
    alignItems: "center",
    margin: 0,
    display: "flex",
    flex: 1,
    "&.Mui-expanded": {
      margin: 0
    }
  },
  "& .MuiAccordionSummary-expandIcon": {
    color: "#587916",
    marginRight: "1rem"
  }
}));

export const AccordionIconSidebar = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "1rem",
  backgroundColor: "#F3F8E6",
  minWidth: "60px",
  "& svg": {
    color: "#587916",
    fontSize: "1.5rem"
  }
});

export const AccordionContent = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "1rem",
  paddingLeft: "1rem",
  paddingRight: "1rem",
  flex: 1,
  justifyContent: "space-between",
  [theme.breakpoints.down("sm")]: {
    gap: "0.5rem",
    flexWrap: "wrap"
  }
}));

export const AccordionLeftContent = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "1rem"
});

export const AccordionRightContent = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "0.75rem",
  [theme.breakpoints.down("sm")]: {
    display: "none"
  }
}));

export const StyledAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
  padding: "1.5rem",
  display: "flex",
  flexDirection: "column",
  gap: "0.5rem",
  backgroundColor: "#FFFFFF",
  [theme.breakpoints.down("md")]: {
    padding: "0.75rem"
  }
}));

export const OutputBatchTitle = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "0.5rem",
  fontFamily: "Roboto",
  fontSize: "1rem",
  fontWeight: 400,
  color: "#757575"
});

export const OutputBatchId = styled(Box)({
  fontFamily: "Inter",
  fontSize: "1.125rem",
  fontWeight: 500,
  color: "#353535"
});

export const StyledChip = styled(Chip)(({ theme }) => ({
  backgroundColor: "#F3F8E6",
  color: "#587916",
  fontWeight: 500,
  fontSize: "0.875rem",
  height: "24px"
}));

export const DataTable = styled(Paper)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  borderRadius: "0.5rem",
  overflow: "hidden",
  [theme.breakpoints.down("md")]: {
    overflowX: "auto",
    overflowY: "hidden",
    "&::-webkit-scrollbar": {
      height: "8px"
    },
    "&::-webkit-scrollbar-track": {
      background: "#f1f1f1"
    },
    "&::-webkit-scrollbar-thumb": {
      background: "#A1CA4F",
      borderRadius: "4px"
    },
    "&::-webkit-scrollbar-thumb:hover": {
      background: "#587916"
    }
  }
}));

export const TableContentWrapper = styled(Box)(({ isLoading }) => ({
  opacity: isLoading ? 0.5 : 1,
  transition: "opacity 0.3s ease",
  pointerEvents: isLoading ? "none" : "auto"
}));

export const StyledLinearProgress = styled(LinearProgress)({
  backgroundColor: "#E5ECD1",
  "& .MuiLinearProgress-bar": {
    backgroundColor: "#A1CA4F"
  }
});

export const TableHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  width: "100%",
  borderBottom: "2px solid #C4C4C4",
  backgroundColor: "#F3F8E6",
  [theme.breakpoints.down("md")]: {
    minWidth: "max-content"
  }
}));

export const TableHeaderCell = styled(Box)(({ theme, flex = 1, sortable }) => ({
  display: "flex",
  flex: flex,
  padding: "1rem",
  justifyContent: "flex-start",
  alignItems: "center",
  gap: "0.25rem",
  borderRight: "1px solid #C4C4C4",
  fontFamily: "Roboto",
  fontSize: "0.875rem",
  fontWeight: 500,
  color: "#757575",
  cursor: sortable ? "pointer" : "default",
  userSelect: "none",
  "&:hover": {
    backgroundColor: sortable ? "#E5ECD1" : "transparent"
  },
  "&:last-child": {
    borderRight: "none"
  },
  [theme.breakpoints.down("md")]: {
    flex: "0 0 auto",
    minWidth: "150px",
    padding: "0.5rem",
    whiteSpace: "nowrap"
  }
}));

export const TableRow = styled(Box)(({ theme }) => ({
  display: "flex",
  width: "100%",
  borderBottom: "1px solid #C4C4C4",
  "&:last-child": {
    borderBottom: "none"
  },
  "&:hover": {
    backgroundColor: "#FAFAFA"
  },
  [theme.breakpoints.down("md")]: {
    minWidth: "max-content"
  }
}));

export const TableCell = styled(Box)(({ theme, flex = 1 }) => ({
  display: "flex",
  flex: flex,
  padding: "0.75rem 1rem",
  justifyContent: "flex-start",
  alignItems: "center",
  borderRight: "1px solid #C4C4C4",
  backgroundColor: "#fff",
  fontFamily: "Roboto",
  fontSize: "0.875rem",
  fontWeight: 400,
  color: "#353535",
  "&:last-child": {
    borderRight: "none"
  },
  [theme.breakpoints.down("md")]: {
    flex: "0 0 auto",
    minWidth: "150px",
    padding: "0.5rem",
    whiteSpace: "nowrap"
  }
}));

export const LoadingContainer = styled(Box)({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  padding: "3rem",
  minHeight: "200px"
});

export const EmptyState = styled(Box)({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  padding: "2rem",
  color: "#757575",
  fontFamily: "Roboto",
  fontSize: "1rem"
});
